/**
 * useCarrierRows / useCarrier — TanStack Query hooks for the Versicherer list
 * + detail header (Story V10-J2).
 *
 * live mode → GET /carriers via the typed client (mapped to Carrier).
 * mock mode → resolves the existing entity-detail CARRIERS mock (mock layer
 *             NOT deleted; UI renders standalone without a backend).
 *
 * [Source: apps/web/src/features/customers/hooks/useKundeRows.ts] (hook pattern)
 */

import { useQuery } from '@tanstack/react-query';
import type { VersichererDetailDto } from '@brokly/shared';
import { qk } from '../../../lib/query-keys';
import { isLiveData } from '../../../lib/data-mode';
import { apiClient } from '../../../lib/api-client';
import { fetchCarrierRows } from '../api/carriers-api';
import { CARRIERS } from '../../entity-detail/mock';
import type { Carrier } from '../../entity-detail/types';

export function useCarrierRows() {
  const live = isLiveData();
  return useQuery<Carrier[]>({
    queryKey: qk.carriers(live ? 'live' : 'mock'),
    queryFn: () => (live ? fetchCarrierRows() : Promise.resolve(CARRIERS)),
    // mock mode: synchronous initialData (no loading flicker, deterministic tests)
    ...(live ? {} : { initialData: CARRIERS, staleTime: Infinity }),
  });
}

/** One carrier by id (carrier_uid in live mode) — selected from the list query. */
export function useCarrier(id: string) {
  const query = useCarrierRows();
  return {
    ...query,
    carrier: query.data?.find((c) => c.id === id),
  };
}

/**
 * The full Versicherer detail (GET /carriers/:uid) — Stammdaten/CRM + register read-through + the
 * linked counts. Live mode only; in mock mode the detail screen falls back to the list-derived
 * useCarrier (P2 hardening 02).
 */
export function useCarrierDetail(uid: string) {
  const live = isLiveData();
  return useQuery<VersichererDetailDto>({
    queryKey: qk.carrier(uid),
    queryFn: () => apiClient.carriers.get(uid),
    enabled: live && uid !== '',
  });
}
